/**
 * Concierge — 30-day report (PROD-6787). Standalone drill-down page reached from the
 * "30-day report" button on /concierge-pods. For each of the last 30 (midnight-IST) days
 * it shows the daily + rolling-30-day cap as in force that day, the pods the advisor was
 * in, and the lifestyle students assigned (with the assignment time + reason).
 */

import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import moment from 'moment-timezone';
import CommonHeader from '../../util_components/common_header';
import CustomLoading from '../../util_components/custom_loading';
import { get_api_url } from '../../../utils/urls';
import { get_api } from '../../../redux/api_funcs';
import * as actions from '../../../redux/action_creators';

const BASE = '/v2/cms_user/concierge_pods';
const PAGE_STYLE = {
  background: 'var(--surface-page)',
  color: 'var(--text-primary)',
  minHeight: '100%'
};
const CARD_STYLE = { background: 'var(--surface-card)', borderColor: 'var(--border-default)' };
const MUTED_STYLE = { color: 'var(--text-muted)' };

class ConciergePodsReport extends Component {
  state = { loading: false, data: null };

  componentDidMount() {
    this.load();
  }

  load = () => {
    const { location, set_notification_variable } = this.props;
    const uuid = new URLSearchParams(location.search).get('teacher') || '';
    if (!uuid) {
      set_notification_variable(true, 'error', 'No advisor specified');
      return;
    }
    this.setState({ loading: true });
    get_api(get_api_url(`${BASE}/concierge_teachers/${uuid}/daily_report`), true)
      .then((res) => this.setState({ data: (res.data && res.data.data) || null, loading: false }))
      .catch((e) => {
        let msg = 'Failed to load report';
        if (e && e.response && e.response.data) msg = e.response.data.reason || msg;
        set_notification_variable(true, 'error', msg);
        this.setState({ loading: false });
      });
  };

  render_student = (s) => (
    <li key={s.uuid} className="text-xs" style={MUTED_STYLE}>
      <a
        href={`/studentprofile?id=${encodeURIComponent(s.uuid)}`}
        target="_blank"
        rel="noopener noreferrer"
        style={{ color: 'var(--brand-primary)' }}
      >
        {[s.first_name, s.last_name].filter(Boolean).join(' ') || s.email || s.uuid}
      </a>
      {s.email ? ` · ${s.email}` : ''}
      {' · '}
      {moment(s.assigned_at).tz('Asia/Kolkata').format('MMM D, h:mm A [IST]')}
      {s.reason ? ` · ${s.reason}` : ''}
    </li>
  );

  render_day = (d) => (
    <div key={d.date} className="rounded-lg border p-3" style={CARD_STYLE}>
      <div className="flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between">
        <span className="text-sm font-semibold">{d.date}</span>
        <div className="flex flex-col gap-0.5 text-xs font-semibold sm:items-end">
          <span
            style={
              d.per_day_limit > 0 && d.count >= d.per_day_limit
                ? { color: 'var(--state-error)' }
                : MUTED_STYLE
            }
          >
            1-day: {d.count} / {d.per_day_limit}
          </span>
          <span
            style={
              d.per_30_days_limit > 0 && d.thirty_count >= d.per_30_days_limit
                ? { color: 'var(--state-error)' }
                : MUTED_STYLE
            }
          >
            30-day: {d.thirty_count} / {d.per_30_days_limit}
          </span>
        </div>
      </div>
      <div className="mt-1 text-xs" style={MUTED_STYLE}>
        Pods: {d.pods && d.pods.length ? d.pods.join(', ') : 'none'}
      </div>
      {d.count > 0 && (
        <ul className="mt-2 flex flex-col gap-0.5">
          {(d.students || []).map(this.render_student)}
        </ul>
      )}
    </div>
  );

  render() {
    const { loading, data } = this.state;
    const t = data && data.teacher;
    const name = t ? `${t.first_name} ${t.last_name}` : 'Concierge';
    return (
      <div style={PAGE_STYLE}>
        <CommonHeader title="Concierge — 30-day report" loading={loading} />
        <div className="mx-auto max-w-4xl px-3 py-4 sm:px-6">
          <Link to="/concierge-pods" className="text-sm" style={{ color: 'var(--brand-primary)' }}>
            &larr; Back to Concierge Pods
          </Link>
          {loading && <CustomLoading />}
          {!loading && data && (
            <div className="mt-3">
              <h2 className="text-lg font-semibold">{name}</h2>
              <div className="mt-1 text-sm" style={MUTED_STYLE}>
                Current: daily cap {data.per_day_limit} · 30-day cap {data.per_30_days_limit} · pods{' '}
                {data.current_pods && data.current_pods.length
                  ? data.current_pods.join(', ')
                  : 'none'}{' '}
                · last-30-day total {data.thirty_day_total}
              </div>
              <div className="mt-1 text-xs" style={MUTED_STYLE}>
                Days reset at midnight IST. Daily caps / pods shown are as in force on each date.
              </div>
              <div className="mt-3 flex flex-col gap-2">
                {(data.days || []).map(this.render_day)}
              </div>
            </div>
          )}
          {!loading && !data && (
            <div className="mt-3 text-sm" style={MUTED_STYLE}>
              No report data.
            </div>
          )}
        </div>
      </div>
    );
  }
}

ConciergePodsReport.propTypes = {
  location: PropTypes.shape({ search: PropTypes.string }).isRequired,
  set_notification_variable: PropTypes.func.isRequired
};

const mapStateToProps = () => ({});

const mapDispatchToProps = (dispatch) => ({
  set_notification_variable: (show, type, mes) =>
    dispatch(actions.set_notification_variable(show, type, mes))
});

export default connect(mapStateToProps, mapDispatchToProps)(ConciergePodsReport);
